import {
  isNationalCodeChecksumValid,
  isShebaChecksumValid,
  MOBILE_E164_PATTERN,
  MOBILE_MASK,
  NATIONAL_CODE_MASK,
  normalizeMobileField,
  normalizeShebaInput,
  SHEBA_DIGITS_MASK,
} from '@saay/formatting';
import { TextField } from '../kit.js';

function mobileTrailingValid(raw: string): boolean {
  return MOBILE_E164_PATTERN.test(normalizeMobileField(raw));
}

/** Iranian mobile with live grouping and early length check (XAP-30). */
export function MobileField({
  error,
  id,
  label,
  onBlur,
  onChange,
  value,
}: {
  readonly error?: string | undefined;
  readonly id?: string;
  readonly label: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
  readonly value: string;
}) {
  const raw = MOBILE_MASK.extractDigits(normalizeMobileField(value));

  return (
    <TextField
      autoComplete="off"
      {...(error === undefined ? {} : { error })}
      {...(id === undefined ? {} : { id })}
      inputMode="tel"
      label={label}
      ltr
      mask={MOBILE_MASK}
      maskRaw={raw}
      {...(onBlur === undefined ? {} : { onBlur })}
      onMaskRawChange={(next) => {
        onChange(next === '' ? '' : MOBILE_MASK.formatDigits(next));
      }}
      trailingValid={raw.length === MOBILE_MASK.maxDigits && mobileTrailingValid(raw)}
    />
  );
}

function nationalCodeTrailingValid(raw: string): boolean {
  return raw.length === NATIONAL_CODE_MASK.maxDigits && isNationalCodeChecksumValid(raw);
}

/** Ten-digit national code with checksum check icon (XAP-30). */
export function NationalCodeField({
  error,
  id,
  label,
  onBlur,
  onChange,
  value,
}: {
  readonly error?: string | undefined;
  readonly id?: string;
  readonly label: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
  readonly value: string;
}) {
  const raw = NATIONAL_CODE_MASK.extractDigits(value);

  return (
    <TextField
      autoComplete="off"
      {...(error === undefined ? {} : { error })}
      {...(id === undefined ? {} : { id })}
      inputMode="numeric"
      label={label}
      ltr
      mask={NATIONAL_CODE_MASK}
      maskRaw={raw}
      {...(onBlur === undefined ? {} : { onBlur })}
      onMaskRawChange={(next) => {
        onChange(next);
      }}
      trailingValid={nationalCodeTrailingValid(raw)}
    />
  );
}

function shebaTrailingValid(raw: string): boolean {
  const compact = normalizeShebaInput(raw.length === 0 ? '' : `IR${raw}`);
  return /^IR\d{24}$/.test(compact) && isShebaChecksumValid(compact);
}

/** Sheba with fixed IR prefix and grouped digits (XAP-30). */
export function ShebaField({
  error,
  id,
  label,
  onBlur,
  onChange,
  value,
}: {
  readonly error?: string | undefined;
  readonly id?: string;
  readonly label: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
  readonly value: string;
}) {
  const raw = SHEBA_DIGITS_MASK.extractDigits(normalizeShebaInput(value).replace(/^IR/i, ''));

  return (
    <TextField
      autoComplete="off"
      {...(error === undefined ? {} : { error })}
      {...(id === undefined ? {} : { id })}
      inputMode="numeric"
      label={label}
      ltr
      mask={SHEBA_DIGITS_MASK}
      maskRaw={raw}
      {...(onBlur === undefined ? {} : { onBlur })}
      onMaskRawChange={(next) => {
        onChange(next === '' ? '' : `IR${next}`);
      }}
      prefix="IR"
      trailingValid={raw.length === SHEBA_DIGITS_MASK.maxDigits && shebaTrailingValid(raw)}
    />
  );
}
